import { ArrowDown } from 'lucide-react';
import { FC, Fragment, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetWhatsappMessagesQuery, useMarkWhatsappConversationReadMutation, type WhatsappConversation } from '@/services/endpoints/whatsappInbox';
import { THREAD_POLLING_MS, chronological, dayLabel, sameDay } from '../inboxUtils';
import { isStopOptOutText, useConversationUiState } from '../useConversationUiState';
import { Composer } from './Composer';
import { MessageBubble } from './MessageBubble';
import { ThreadBanners, ThreadHeader } from './ThreadChrome';

/** Closer than this to the bottom counts as "at the bottom": the thread then follows new messages. */
const NEAR_BOTTOM_PX = 80;
/** Messages from the same side this close together read as one group: tighter spacing, one square corner. */
const GROUP_MS = 5 * 60_000;

const scrollToEnd = (element: HTMLElement | null) => {
  if (element) element.scrollTop = element.scrollHeight;
};

export const ConversationThread: FC<{ conversation: WhatsappConversation; onBack?: VoidFunction }> = ({ conversation, onBack }) => {
  const { t } = useTranslation('whatsappInbox');
  const workspace = useWorkspace();
  const { data } = useGetWhatsappMessagesQuery({ workspaceId: workspace.id, conversationId: conversation.id }, { pollingInterval: THREAD_POLLING_MS });
  const [markRead] = useMarkWhatsappConversationReadMutation();
  const { optedOut, setOptedOut } = useConversationUiState(conversation.id);
  const list = useRef<HTMLOListElement>(null);
  const messages = chronological(data);
  const last = messages[messages.length - 1];
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  const following = useRef(true);
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const [, tick] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);

  useEffect(() => {
    if (visible && conversation.unreadCount > 0) {
      void markRead({ workspaceId: workspace.id, conversationId: conversation.id });
    }
  }, [visible, conversation.id, conversation.unreadCount, markRead, workspace.id]);

  useEffect(() => {
    if (optedOut) return;
    const hasInboundStop = messages.some((message) => message.direction === 'inbound' && isStopOptOutText(message.text));
    if (hasInboundStop) setOptedOut(true);
  }, [messages, optedOut, setOptedOut]);

  useEffect(() => {
    if (following.current || last?.direction === 'outbound') scrollToEnd(list.current);
  }, [conversation.id, last?.id, last?.direction]);

  useEffect(() => {
    const element = list.current;
    if (!element) return undefined;
    const keepBottom = () => {
      if (following.current) scrollToEnd(element);
    };
    element.addEventListener('load', keepBottom, true);
    element.addEventListener('loadedmetadata', keepBottom, true);
    return () => {
      element.removeEventListener('load', keepBottom, true);
      element.removeEventListener('loadedmetadata', keepBottom, true);
    };
  }, []);

  const onScroll = () => {
    const element = list.current;
    if (!element) return;
    following.current = element.scrollHeight - element.scrollTop - element.clientHeight <= NEAR_BOTTOM_PX;
    setAwayFromBottom(!following.current);
  };

  const jumpToBottom = () => {
    following.current = true;
    setAwayFromBottom(false);
    scrollToEnd(list.current);
  };

  return (
    <section className='flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden'>
      <div className='shrink-0'>
        <ThreadHeader conversation={conversation} onBack={onBack} />
        <ThreadBanners conversation={conversation} />
      </div>
      <div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
        <ol ref={list} className='flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto overscroll-contain bg-primary-background px-5 py-4' onScroll={onScroll}>
          {messages.map((message, index) => {
            const previous = messages[index - 1];
            const newDay = !previous || !sameDay(previous.sentAt, message.sentAt);
            const grouped = !newDay && previous.direction === message.direction && Date.parse(message.sentAt) - Date.parse(previous.sentAt) < GROUP_MS;
            return (
              <Fragment key={message.id}>
                {newDay ? (
                  <li className='flex justify-center py-2'>
                    <span className='rounded-lg bg-white-100 px-2.5 py-1 text-[11px] font-semibold text-primary-900/60 shadow-sm'>
                      {dayLabel(message.sentAt, { today: t('today'), yesterday: t('yesterday') })}
                    </span>
                  </li>
                ) : null}
                <MessageBubble message={message} grouped={grouped} />
              </Fragment>
            );
          })}
        </ol>
        {awayFromBottom ? (
          <button
            type='button'
            aria-label={t('scrollToBottom')}
            title={t('scrollToBottom')}
            className='absolute bottom-3 right-4 flex size-9 items-center justify-center rounded-full bg-white-100 text-primary-900/70 shadow-md hover:text-primary-900'
            onClick={jumpToBottom}
          >
            <ArrowDown className='size-4' />
          </button>
        ) : null}
      </div>
      <div className='shrink-0'>
        <Composer conversation={conversation} />
      </div>
    </section>
  );
};
